import React, { useState, useEffect } from 'react';
import {
    Box,
    IconButton,
    Typography,
    Avatar,
    Paper,
    Divider,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,

} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useService } from 'contexts/ServiceContextProvider';
import { useAuthorization } from 'contexts/AuthContextProvider';
import { useWorkspaces } from 'contexts/WorkspacesContextProvider';
import { MentionsInput, Mention } from 'react-mentions';
import { useTranslation } from "react-i18next";
import "../../css/components/Core/TaskComments.css";
import "../../css/main.css";
import TinyCustomFallback from 'components/Custom/TinyCustomFallback';
import { DateTime } from 'luxon';
import SkeletonComment from '../Custom/Skeletons/CommentSkeleton';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from "@mui/icons-material/Warning";


const renderCommentWithMentions = (text) => {
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;

    return text.replace(mentionRegex, (match, display, id) => {
        return `<a href="/workflow/profile/${id}">@${display}</a>`;
    });
};

export default function Comments({ task_id, project_id, myUserTimezone, taskComments, hasMoreComments, setHasMoreComments, updateCommentsState, fetchAllActivityLogs}) {
    const { t } = useTranslation();

    const { PostTaskComment, fetchMoreComments, fetchMoreReplies, UpdateTaskComment, DeleteTaskComment } = useService();
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [replyComment, setReplyComment] = useState({});
    const { getLoggedUser } = useAuthorization();
    const loggedUserId = getLoggedUser();
    const [showReplyBoxForCommentId, setShowReplyBoxForCommentId] = useState(null); // Track reply box for a specific comment

    const [avoidMisfire, setAvoidMisfire] = useState(false);

    const workspaceContext = useWorkspaces();
    const myWorkspaces = workspaceContext.workspaces;
    const workspaceWorkers = workspaceContext.workspaceWorkers;

    const [isLoadingComments, setIsLoadingComments] = useState(false);
    const [isLoadingReplies, setLoadingReplies] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const loggedWorker = workspaceWorkers.find(w => w.id === loggedUserId);
    const isWorkspaceAdmin = loggedWorker?.is_admin || loggedWorker?.is_owner

    /* eslint-disable react-hooks/exhaustive-deps */
   useEffect(() => {
    const normalizeDeleted = (comments) => {
        return comments.map(c => ({
            ...c,
            isDeletedUI:
                c.is_deleted_by_admin ||
                c.text === "[Original comment was deleted]" ||
                c.text === "[Original comment deleted by an admin]",
            replies: c.replies ? normalizeDeleted(c.replies) : [],
        }));
    };

    setComments(normalizeDeleted(taskComments));
    setIsLoading(false);
}, [taskComments]);


    const handleLoadMoreComments = async () => {
        setIsLoadingComments(true);
        try {
            const { data } = await fetchMoreComments(myWorkspaces[0].id, project_id, task_id);
            const newComments = data.filter(
                (newComment) => !comments.some((existingComment) => existingComment.id === newComment.id)
            );
            setComments((prevComments) => [...newComments, ...prevComments]);
            setHasMoreComments(false);
        } catch (error) {
            console.error('Error loading more comments:', error);
        } finally {
            setIsLoadingComments(false);
        }
    };

    const handleLoadMoreReplies = async (commentId) => {
        setLoadingReplies((prev) => ({ ...prev, [commentId]: true }));
        try {
            const { data: replies, has_more } = await fetchMoreReplies(myWorkspaces[0].id, project_id, task_id, commentId);

            setComments((prevComments) =>
                prevComments.map((comment) => {
                    if (comment.id === commentId) {
                        const newReplies = replies.filter(
                            (newReply) => !comment.replies.some((existingReply) => existingReply.id === newReply.id)
                        );

                        return {
                            ...comment,
                            replies: [...newReplies, ...comment.replies],
                            has_more_replies: has_more
                        };
                    }
                    return comment;
                })
            );
        } catch (error) {
            console.error('Error loading more replies:', error);
        } finally {
            setLoadingReplies((prev) => ({ ...prev, [commentId]: false }));
        }
    };

const handleUpdateComment = async (commentId, newText, parentId = null) => {
        const mentionedIds = extractMentionedIds(newText);
        try {
            const updatedComment = await UpdateTaskComment(myWorkspaces[0].id, project_id, task_id, commentId, {
                text: newText,
                mentioned_users: mentionedIds,
            });

            if (parentId) {
                setComments(prev => prev.map(c => c.id === parentId
                    ? { ...c, replies: c.replies.map(r => r.id === commentId ? updatedComment : r) } : c));
            } else {
                setComments(prev => prev.map(c => c.id === commentId ? { ...c, ...updatedComment } : c));
            }

            if (typeof fetchAllActivityLogs === "function") {
                await fetchAllActivityLogs();
            }

        } catch (error) {
            console.error('Error updating comment:', error);
        }
    };

    const handleDeleteComment = async (commentId, parentId = null) => {
        try {
            const targetComment = comments.find(c => c.id === commentId)
                || comments.flatMap(c => c.replies || []).find(r => r.id === commentId);

            const deletedByAdmin = isWorkspaceAdmin && targetComment?.user?.id !== loggedUserId;

            await DeleteTaskComment(myWorkspaces[0].id, project_id, task_id, commentId);

            if (parentId) {
                setComments(prev => prev.map(c => {
                    if (c.id === parentId) {
                        const updatedReplies = c.replies.filter(r => r.id !== commentId);

                        if (c.isDeletedUI && updatedReplies.length === 0) {
                            return null;
                        }

                        return {
                            ...c,
                            replies: updatedReplies
                        };
                    }
                    return c;
                }).filter(Boolean));

        } else {
                setComments(prev => prev.map(c => {
                    if (c.id === commentId) {
                        if (c.replies && c.replies.length > 0) {
                            return {
                                ...c,
                                text: deletedByAdmin
                                    ? t("modal.comment_deleted_by_admin")
                                    : t("modal.comment_deleted"),
                                isDeletedUI: true,
                            };
                        }
                        return null;
                    }
                    return c;
                }).filter(Boolean));
            }

            if (typeof fetchAllActivityLogs === "function") {
                await fetchAllActivityLogs();
            }
        } catch (error) {
            console.error('Error deleting comment:', error);
        }
    };

    const extractMentionedIds = (text) => {
        const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
        const mentionedIds = [];
        let match;

        while ((match = mentionRegex.exec(text)) !== null) {
            mentionedIds.push(match[2]);
        }

        return mentionedIds;
    };

    const handlePostComment = async () => {
        if (newComment.trim() === '' || avoidMisfire) return;
        setAvoidMisfire(true);
        updateCommentsState(true);
        const mentionedIds = extractMentionedIds(newComment);
        try {
            const commentData = {
                text: newComment,
                parent_comment: null,
                user_id: loggedUserId,
                mentioned_users: mentionedIds,
            };
            const postedComment = await PostTaskComment(myWorkspaces[0].id, project_id, task_id, commentData);
            setComments((prev) => [postedComment, ...prev]);
            setNewComment('');

            if (typeof fetchAllActivityLogs === "function") {
                await fetchAllActivityLogs();
            }

        } catch (error) {
            console.error('Error posting comment:', error);
        } finally {
            setTimeout(() => setAvoidMisfire(false), 1000);
        }
    };

    const handlePostReply = async (parentId) => {
        const replyText = replyComment[parentId]?.trim();
        if (!replyText) return;

        const mentionedIds = extractMentionedIds(replyText);

        try {
            const replyData = {
                text: replyText,
                parent_comment: parentId, // Set the parent_comment ID for replies
                user_id: getLoggedUser(),
                mentioned_users: mentionedIds,
            };
            const postedReply = await PostTaskComment(myWorkspaces[0].id, project_id, task_id, replyData);

            const updatedComments = comments.map((comment) => {
                if (comment.id === parentId) {
                    return {
                        ...comment,
                        replies: [...comment.replies, postedReply] // Add the new reply to the list
                    };
                }
                return comment;
            });

            setComments(updatedComments);
            setReplyComment({ ...replyComment, [parentId]: '' });
            setShowReplyBoxForCommentId(null); // Hide reply box after posting the reply
        } catch (error) {
            console.error('Error posting reply:', error);
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handlePostComment();
        }
    };

    const handleReplyKeyPress = (event, parentId) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handlePostReply(parentId);
        }
    };

    const mentionData = workspaceWorkers.map(worker => ({
        id: worker.id,
        display: worker.full_name,
        image: worker.image,
    }));

    const handleShowReplyBox = (commentId) => {
        setShowReplyBoxForCommentId(commentId); // Show reply box for the selected comment
    };

    if (isLoading) {
        return <SkeletonComment />;
    }
    return (
        <Box className={`comments-margin ${!taskComments?.length && newComment === "" ? "opacity-box" : ""}`}>
            <Typography variant="h6">{t("modal.comments_title")}</Typography>
            <Divider className="comments-divider" />

            {isLoadingComments ? (
                <>
                    <Button>{t("modal.loading_comments")} <TinyCustomFallback /> </Button>
                </>
            ) : (
                hasMoreComments && (
                    <Button onClick={handleLoadMoreComments}>{t("modal.load_more_comments")}</Button>
                )
            )}

            {comments.length > 0 ? (
                comments.map((comment) => (
                    <Box key={comment.id} className="comments-box">
                        <Comment
                            comment={comment}
                            onReply={(text) =>
                                setReplyComment({ ...replyComment, [comment.id]: text })
                            }
                            replyText={replyComment[comment.id] || ''}
                            onPostReply={() => handlePostReply(comment.id)}
                            onReplyKeyPress={(e) => handleReplyKeyPress(e, comment.id)}
                            mentionData={mentionData}
                            showReplyBox={showReplyBoxForCommentId === comment.id} // Conditionally show reply box
                            onShowReplyBox={() => handleShowReplyBox(comment.id)} // Handle reply box toggle
                            t={t}
                            myUserTimezone={myUserTimezone}
                            handleLoadMoreReplies={handleLoadMoreReplies}
                            isLoadingReplies={isLoadingReplies[comment.id]}
                            onUpdate={handleUpdateComment}
                            onDelete={handleDeleteComment}
                            loggedUserId={loggedUserId}
                            isWorkspaceAdmin={isWorkspaceAdmin}
                        />
                        <Divider />

                    </Box>

                ))
            ) : (
                <Typography>{t("modal.no_comments")}</Typography>
            )}

            <Typography variant="h6">{t("modal.leave_comment")}</Typography>
            <Paper className="comments-paper">
                <MentionsInput
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="mentions"
                    onKeyPress={handleKeyPress}
                    placeholder={t("modal.write_comments")}
                >
                    <Mention
                        trigger="@"
                        data={mentionData}
                        className="comments-mention-input"
                        renderSuggestion={(suggestion, search, highlightedDisplay) => (
                            <Box className="comments-flex">
                                <Avatar
                                    src={`${suggestion.image}`}
                                    className="comments-avatar"
                                />
                                <Typography variant="body2">{highlightedDisplay}</Typography>
                            </Box>
                        )}
                        displayTransform={(id, display) => `@${display}`}
                    />
                </MentionsInput>
                <IconButton
                    className="comments-icon-button"
                    onClick={handlePostComment}
                    disabled={avoidMisfire}
                >
                    <SendIcon className={avoidMisfire ? "black-txt-color" : "white-txt-color"} />
                </IconButton>
            </Paper>
        </Box>
    );
}

const Comment = ({ comment, onReply, replyText, onPostReply, onReplyKeyPress, mentionData, showReplyBox, onShowReplyBox, t, myUserTimezone, handleLoadMoreReplies, isLoadingReplies, onUpdate, onDelete, loggedUserId, isWorkspaceAdmin, onChildEditingChange, parentId = null }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(comment.text);
    const [editingChildCount, setEditingChildCount] = useState(0);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const created = DateTime.fromISO(comment.created_at);
    const updated = DateTime.fromISO(comment.updated_at);
    const isEdited = updated.diff(created, 'seconds').seconds > 2;
    const isDeleted = comment.isDeletedUI;

    const toggleEdit = (status) => {
        setIsEditing(status);

        if (status) {
            setEditText(comment.text);
        }

        if (!status) {
            setEditText(comment.text);
        }

        if (onChildEditingChange) {
            onChildEditingChange(status);
        }
    };

    const handleSaveEdit = () => {
        const hasChanged = editText.trim() !== comment.text.trim();

        if (hasChanged) {
            onUpdate(comment.id, editText, parentId);
        }

        toggleEdit(false);
    };

    const confirmDelete = async () => {
        setIsDeleting(true);
        await onDelete(comment.id, parentId);
        setOpenDeleteModal(false);
        setIsDeleting(false);
    };

   return (
        <Box className="comments-comment-padding">
            <Box className="comments-flex">
                <Avatar src={`${comment.user.image}`} className="comments-avatar-margin">
                    {comment.user?.full_name ? comment.user.full_name[0] : comment.user?.username?.[0]}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1">
                            {comment.user.full_name} - {DateTime.fromISO(comment.created_at, { zone: 'utc' }).setZone(myUserTimezone).toFormat('dd/MM/yyyy HH:mm')}
                        </Typography>
                        {(comment.user.id === loggedUserId || isWorkspaceAdmin) && !isEditing && !isDeleted && (
                            <Box>
                                <IconButton size="small" disabled={isDeleted} onClick={() => toggleEdit(true)}><EditIcon className="comments-edit-icon" fontSize="small" /></IconButton>
                                <IconButton size="small" disabled={isDeleted} onClick={() => setOpenDeleteModal(true)}  ><DeleteIcon fontSize="small" color="error" /></IconButton>
                            </Box>
                        )}
                    </Box>
                    {isEditing ? (
                        <Box>
                            <MentionsInput value={editText} onChange={(e) => setEditText(e.target.value)} className="mentions">
                                <Mention trigger="@" data={mentionData} displayTransform={(id, display) => `@${display}`} />
                            </MentionsInput>
                            <Box style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                                <Button className="accept-button" onClick={handleSaveEdit}>
                                    <CheckCircleIcon style={{ marginRight: 4 }}/> {t("core.save")}
                                </Button>
                                <Button className="decline-button" onClick={() => toggleEdit(false)}>
                                    <CancelIcon style={{ marginRight: 4 }} /> {t("core.cancel")}
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography
                                variant="body2"
                                className="comments-comment-text"
                                dangerouslySetInnerHTML={{ __html: renderCommentWithMentions(comment.text) }}
                            />
                            {isEdited && !isDeleted && (
                                <Typography variant="caption" sx={{ fontStyle: "italic", opacity: 0.7 }}>
                                    {comment?.is_edited_by_admin ? t("modal.edited_by_admin") : t("modal.edited")}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>

        {comment.has_more_replies && (
            isLoadingReplies ? (
                <Box>
                    <Button disabled>{t("modal.loading_replies")}</Button>
                    <TinyCustomFallback />
                </Box>
            ) : (
                <Box className="comments-reply-button">
                    <Button className="comments-reply-button" onClick={() => handleLoadMoreReplies(comment.id)}>
                        {t("modal.load_more_replies")}
                    </Button>
                </Box>
            )
        )}
            {comment.replies?.length > 0 && (
                <Box className="comments-reply-padding">
                    {comment.replies.map((reply) => (
                        <Comment
                            key={reply.id}
                            comment={reply}
                            parentId={comment.id}
                            t={t}
                            myUserTimezone={myUserTimezone}
                            onUpdate={onUpdate}
                            onDelete={onDelete}
                            loggedUserId={loggedUserId}
                            mentionData={mentionData}
                            isWorkspaceAdmin={isWorkspaceAdmin}
                            onChildEditingChange={(status) => {
                                setEditingChildCount(prev => status ? prev + 1 : Math.max(0, prev - 1));
                            }}
                        />
                    ))}
                </Box>
            )}

            {!showReplyBox && !parentId && !isEditing && editingChildCount === 0 && (
                <Box className="comments-reply-button">
                    <Button onClick={onShowReplyBox} variant="text" className="button-dark-bg">{t("core.reply")}</Button>
                </Box>
            )}

            {showReplyBox && !isEditing && (
                <Box className="comments-reply-box">
                    <MentionsInput value={replyText} onChange={(e) => onReply(e.target.value)} className="mentions" onKeyPress={onReplyKeyPress} placeholder={t("core.reply_placeholder")}>
                        <Mention trigger="@" data={mentionData} className="comments-mention-input" displayTransform={(id, display) => `@${display}`} />
                    </MentionsInput>
                    <IconButton className="comments-icon-button" onClick={onPostReply}><SendIcon className="white-txt-color" /></IconButton>
                </Box>
            )}

            <DeleteCommentModal
                open={openDeleteModal}
                handleClose={() => setOpenDeleteModal(false)}
                onDelete={confirmDelete}
                avoidMisfire={isDeleting}
                t={t}
            />
        </Box>
   );
};
const DeleteCommentModal = ({ open, handleClose, onDelete, avoidMisfire, t }) => {
    return (
        <Dialog open={open} onClose={handleClose} className="modal-background-color">
            <DialogTitle>{t("modal.warning")}</DialogTitle>
            <DialogContent className="white-txt-color">
                {t("modal.comment_delete_confirmation")}
            </DialogContent>
            <DialogContent className="delete-resource-dialog-content">
                <WarningIcon className="delete-resource-warning-margin-right" color="warning"/>
                {t("modal.irreversible")}
                <WarningIcon className="delete-resource-warning-margin-left" color="warning"/>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} className="button-dark-bg">
                    {t("global.cancel")}
                </Button>
                <Button
                    onClick={onDelete}
                    disabled={avoidMisfire}
                    className="button-dark-bg"
                    sx={{ color: '#ff1744' }}
                >
                    {t("modal.delete")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
